import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { addMinutes, differenceInMinutes, format, isBefore, parse } from 'date-fns';
import {
  fullNameToDrLastName,
  fullNameToDrLastNameMiddleEast,
  fullNameToLastNameRest,
  isDimmed,
  isNoShow,
  NormalizedStatus,
  normalizeStatus,
} from '../shared/procedureUtils';
import has from 'lodash/has';
import { translateToTz } from '@/util/dateTime';
import HighlightedText from '../HighlightedText';
import { useScope } from '@/hooks/useScope';
import { Procedure as ProcedureT } from '@/types/Procedure';
import isMiddleEast from '@/util/isMiddleEast';
import { isPreOpAsDPUOptimized } from '../../tablet/utils';
import { Box, lighten } from '@material-ui/core';
import { getInOrStatusLabel } from '../statusUtils';
import { formatDuration } from '@/util/duration';
import Chip from '@material-ui/core/Chip';
import { alpha, makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import CardCompactHeader from './components/CardCompactHeader';
import CardTiny from './components/CardTiny';
import { StaffSlotProcedureContext } from '../staff/StaffSlotContext';
import StaffShifts from '../staff/StaffShifts';
import StaffShiftMenu from '../staff/StaffShiftMenu';
import { StaffShiftProcedureContext } from '../staff/StaffShiftContext';
import { unparsedAllergiesSummary } from '@/allergies/AllergiesView';
import { PrimarySpecialty } from '@/types/StaffMember';
import CardNote from '../shared/CardNote';
import { useScreenConfiguration } from '@/contexts/ScreenConfigurationContext';
import Typography from '@material-ui/core/Typography';
import { getRoomTime } from '@/util/procedureEvents';
import { useStatusDisplay } from '@/hooks/useStatusColor';

const Card = styled.div<any>`
  width: ${props => (props.fullHeight ? 'min(fit-content, 100rem)' : '100%')};
  position: absolute;
  left: 0;
  padding: ${props => (props.editMode ? '0 -.2em .375em -.2em' : '0 0.375em')};
  font-weight: 600;
  top: calc(0.4rem + ${props => props.top}%);
  height: ${props => props.length}%;
  box-sizing: border-box;
  z-index: ${props => (props.fullHeight ? 500000 : 1)};
  min-width: ${props => (props.fullHeight ? '100%' : 'auto')};
`;

const StyledChip = styled.div<any>`
  position: absolute;
  top: calc(0.4rem + ${props => props.top}%);
  z-index: 100;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const Header = styled.div<any>`
  display: flex;
  background-color: rgba(255, 255, 255, 0.05);
  box-sizing: border-box;
  font-size: 1em;
  flex-direction: column;
  position: relative;
  z-index: 1;

  ${props =>
    props.isInactive
      ? css`
          opacity: 0.3;
        `
      : null}

  ${props =>
    props.isDisabled
      ? css`
          opacity: 0.3;
          pointer-events: none;
          border: none;
          animation: none;
        `
      : null}

  ${props =>
    props.stretch
      ? css`
          height: 100%;
        `
      : null}

  ${props =>
    props.row
      ? css`
          flex-direction: row;
        `
      : null}

  ${props =>
    props.column
      ? css`
          flex-direction: column;
          height: 100%;

          > * {
            flex: 1 0 auto;
            padding-top: 0;
            padding-bottom: 0;
          }
        `
      : null}
`;

const Body = styled.div<any>`
  padding: 0.4em;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  opacity: ${props => (props.inactive ? '.35' : 1)};

  label {
    opacity: ${props => (props.warning ? 1 : '.35')};
  }
`;

const StartAndEndTime = styled.div<any>`
  display: flex;
  flex-direction: column;
  padding-left: 0.25em;
  padding-right: 0.25em;
  background-color: rgba(255, 255, 255, 0.1);
  margin-right: 1px;
  min-width: 3em;
`;

const StaffMembersBox = styled.div<any>`
  display: flex;
  flex-direction: column;
  padding-left: 0.25em;
  padding-right: 0.25em;
`;

const BodySegment = styled.div`
  display: flex;
  margin-bottom: 0.25em;
`;

export const getDisposition = (dayStart: Date, current: Date | number | null | undefined, dayEnd: Date) => {
  if (current) {
    return (differenceInMinutes(current, dayStart) * 100) / differenceInMinutes(dayEnd, dayStart);
  } else {
    return 0;
  }
};

const getRelativeDuration = (dayStart: Date, duration: number | string, dayEnd: Date) => {
  if (typeof duration === 'number') {
    if (duration) {
      return (duration * 100) / differenceInMinutes(dayEnd, dayStart);
    } else {
      return 10;
    }
  } else {
    return duration;
  }
};

const FULL_HEIGHT_DURATION = 'auto';
const secondInMili = 1000;

export const TurnoverChip: FC<{
  dayStart: Date;
  dayEnd: Date;
  startTimePrevProcedure: string;
  durationPrevProcedure: number;
  startTimeCurrentProcedure: string;
  orOutAt?: string;
  orInAt?: string;
}> = ({
  startTimePrevProcedure,
  durationPrevProcedure,
  startTimeCurrentProcedure,
  dayStart,
  dayEnd,
  orOutAt,
  orInAt,
}) => {
  const scope = useScope();

  const startTime1 = useMemo(
    () => translateToTz(scope?.hospital?.timezone?.id)(parse(startTimePrevProcedure)),
    [scope?.hospital?.timezone?.id, startTimePrevProcedure]
  );
  const startTime2 = useMemo(
    () => translateToTz(scope?.hospital?.timezone?.id)(parse(startTimeCurrentProcedure)),
    [scope?.hospital?.timezone?.id, startTimeCurrentProcedure]
  );
  const endTime = addMinutes(startTime1, durationPrevProcedure);

  const duration = useMemo(() => formatDuration(orOutAt, orInAt, true), [orInAt, orOutAt]);

  const minutes = differenceInMinutes(startTime2, endTime) / 2 - 15;

  const disposition = getDisposition(dayStart, addMinutes(endTime, minutes), dayEnd);
  const relativeDuration = getRelativeDuration(dayStart, 15, dayEnd);

  if (!orInAt || !orOutAt || isBefore(orInAt, orOutAt)) return null;

  return (
    <StyledChip top={disposition} length={relativeDuration}>
      <Chip variant="outlined" size="small" label={duration} />
    </StyledChip>
  );
};

const minDurationForStaffMembersToShow = 35;

const Procedure: FC<{
  procedure: ProcedureT;
  dayStart: Date;
  dayEnd: Date;
  onClick?: () => void;
  editableStaff?: boolean;
  editMode: boolean;
  isTouchDevice: boolean;
  showBedNumber: boolean;
  isKiosk?: boolean;
  date: Date;
  inlineText?: boolean;
  isFlipRoom?: boolean;
}> = ({
  procedure,
  dayStart,
  dayEnd,
  onClick,
  editableStaff = false,
  editMode,
  isTouchDevice,
  showBedNumber,
  isKiosk,
  date,
  inlineText,
  isFlipRoom,
}) => {
  const scope = useScope();
  const hospitalId = scope?.hospital?.id;
  const canAddStaff = !isKiosk && editableStaff;
  const { getColor, getContourStyle, getFlashingContourColor } = useStatusDisplay('schedule');

  const classes = useStyles();

  const [fullHeight, setFullHeight] = useState(false);
  const fullHeightRef = useRef(fullHeight);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { highlightNotes, hideNotes, highlightAllergies, wrapProcedureName, timelineViewRoomTime } =
    useScreenConfiguration();

  const handleMouseEnter = useCallback(() => {
    if (fullHeightRef.current || hoverTimeoutRef.current) {
      return;
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setFullHeight(true);
      fullHeightRef.current = true;
      hoverTimeoutRef.current = null;
    }, secondInMili);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setFullHeight(false);
    fullHeightRef.current = false;
  }, []);

  const handleOnClick = useCallback(() => {
    if (isTouchDevice) {
      setFullHeight(prev => {
        const next = !prev;
        fullHeightRef.current = next;
        return next;
      });
      setTimeout(() => {
        setFullHeight(false);
        fullHeightRef.current = false;
      }, 10 * secondInMili);
    }
    onClick?.();
  }, [isTouchDevice, onClick]);

  useEffect(() => {
    fullHeightRef.current = fullHeight;
  }, [fullHeight]);

  useEffect(
    () => () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    },
    []
  );

  const startTime = useMemo(() => {
    if (procedure.startTime) {
      return translateToTz(scope?.hospital?.timezone?.id)(parse(procedure.startTime));
    }
    return undefined;
  }, [procedure.startTime, scope?.hospital?.timezone?.id]);
  const duration = !isTouchDevice
    ? procedure.duration || 0
    : !fullHeight
      ? procedure.duration || 0
      : FULL_HEIGHT_DURATION;
  const normalizedStatus = useMemo(
    () => normalizeStatus(procedure.patient?.status, procedure.patient?.room, isMiddleEast(hospitalId)),
    [hospitalId, procedure.patient?.room, procedure.patient?.status]
  );
  const status = has(procedure, 'patient.id') ? normalizedStatus : 'PreAdm';

  const formattedDate = useMemo(() => format(date, 'YYYY-MM-DD'), [date]);

  const noShow = isNoShow(procedure);
  const isInactive = isDimmed(procedure);
  const isCanceled = procedure.isCanceled;

  const physicianName = isMiddleEast(hospitalId)
    ? fullNameToDrLastNameMiddleEast(procedure.physician?.name)
    : fullNameToDrLastName(procedure.physician?.name);
  const color = procedure.physician?.color || procedure.physicianColor || '#888888';

  const anesthesiologistName = isMiddleEast(hospitalId)
    ? fullNameToDrLastName(procedure.anesthesiologist?.name)
    : fullNameToLastNameRest(procedure.anesthesiologist?.name);
  const anesthesiaType = procedure.anesthesiaType;

  const isDPU = useMemo(
    () => isPreOpAsDPUOptimized(procedure.patient?.room?.type, procedure.patient?.events),
    [procedure.patient?.events, procedure.patient?.room?.type]
  );
  const isHelp = useMemo(
    () => Boolean(procedure.patient?.events?.helpActiveAt),
    [procedure.patient?.events?.helpActiveAt]
  );
  const procedureColor = procedure.color || getColor(status, hospitalId, null) || getFlashingContourColor(status);

  const allergies = useMemo(() => unparsedAllergiesSummary(procedure.allergies), [procedure.allergies]);

  const roomTime = useMemo(() => getRoomTime(procedure), [procedure]);

  if (typeof duration === 'number' && (duration || 0) <= 15) {
    return (
      <Card
        fullHeight={fullHeight}
        editMode={editMode}
        top={getDisposition(dayStart, startTime, dayEnd)}
        length={getRelativeDuration(dayStart, 15, dayEnd)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Box
          className={clsx(classes.helpContainer, {
            [classes.helpContainerAnimation]:
              isHelp && ![NormalizedStatus.Completed, NormalizedStatus.Canceled].includes(normalizedStatus),
          })}
        >
          <Box
            className={clsx(classes.wrapper, {
              [classes.disabled]: [NormalizedStatus.Completed, NormalizedStatus.Canceled].includes(normalizedStatus),
              [classes.onClick]: !isInactive && onClick,
              [classes.isDpuFromOr]: isDPU,
              [classes.customBorderColor]: procedureColor,
            })}
            style={
              procedureColor && !isHelp
                ? { borderColor: procedureColor, ...getContourStyle(status, procedureColor) }
                : undefined
            }
            onClick={handleOnClick}
          >
            <Header
              stretch
              isInactive={isInactive}
              isDisabled={[NormalizedStatus.Completed, NormalizedStatus.Canceled].includes(normalizedStatus)}
            >
              <CardTiny
                procedure={procedure}
                physicianName={physicianName}
                anesthesiologistName={anesthesiologistName}
                anesthesiaType={anesthesiaType}
                showBedNumber={showBedNumber}
                color={color}
                status={status}
                hospitalId={hospitalId}
                inOrStatus={
                  normalizedStatus === NormalizedStatus.In_OR && getInOrStatusLabel(procedure.patient?.status)
                }
                roomTime={roomTime}
                timelineViewRoomTime={timelineViewRoomTime}
              />
            </Header>
          </Box>
        </Box>
      </Card>
    );
  } else {
    return (
      <StaffShiftProcedureContext
        hospitalId={hospitalId}
        procedureId={procedure.id}
        date={formattedDate}
        staffShifts={procedure.staffShifts || []}
      >
        <Card
          fullHeight={fullHeight}
          editMode={editMode}
          top={getDisposition(dayStart, startTime, dayEnd)}
          length={getRelativeDuration(
            dayStart,
            typeof duration === 'number' ? (duration <= 30 ? minDurationForStaffMembersToShow : duration) : duration,
            dayEnd
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Box
            className={clsx(classes.helpContainer, {
              [classes.helpContainerAnimation]:
                isHelp &&
                !isInactive &&
                ![NormalizedStatus.Completed, NormalizedStatus.Canceled].includes(normalizedStatus),
              [classes.helpContainerBackground]: fullHeight,
            })}
          >
            <Box
              className={clsx(classes.wrapper, {
                [classes.onClick]: !isInactive && onClick,
                [classes.isDpuFromOr]: isDPU,
                [classes.customBorderColor]: procedureColor,
              })}
              style={
                procedureColor && !isHelp
                  ? { borderColor: procedureColor, ...getContourStyle(status, procedureColor) }
                  : undefined
              }
              onClick={handleOnClick}
            >
              <Box
                className={clsx(classes.header, {
                  // [classes.stretch]: (duration || 0) <= 30,
                  [classes.row]: true,
                  [classes.completed]: isInactive,
                  [classes.disabled]: [NormalizedStatus.Completed, NormalizedStatus.Canceled].includes(
                    normalizedStatus
                  ),
                })}
              >
                <StartAndEndTime>
                  <div>{procedure.startTimeText}</div>
                  <div>{procedure.endTimeText}</div>
                </StartAndEndTime>
                <Box
                  className={clsx(classes.header, {
                    // [classes.stretch]: (duration || 0) <= 30,
                    [classes.column]: true,
                  })}
                  style={{ flex: '1', height: 'auto', minWidth: 0 }}
                >
                  <CardCompactHeader
                    procedure={procedure}
                    physicianName={physicianName}
                    anesthesiologistName={anesthesiologistName}
                    anesthesiaType={procedure.anesthesiaType}
                    showBedNumber={showBedNumber}
                    color={procedure.physician?.color || procedure.physicianColor || '#888888'}
                    status={isCanceled ? 'Canceled' : noShow ? 'NoShow' : status}
                    hospitalId={hospitalId}
                    inOrStatus={
                      normalizedStatus === NormalizedStatus.In_OR && getInOrStatusLabel(procedure.patient?.status)
                    }
                    roomTime={roomTime}
                    isKiosk={isKiosk}
                    timelineViewRoomTime={timelineViewRoomTime}
                    fullHeight={fullHeight}
                    isFlipRoom={isFlipRoom}
                  />
                </Box>
              </Box>
              {((typeof duration === 'number' && duration > 30) || duration === 'auto') && (
                <Body inactive={isInactive}>
                  {(wrapProcedureName || fullHeight) && (
                    <Typography className={classes.procedureName}>
                      {procedure.procedureType?.replaceAll('\n', ' ')}
                    </Typography>
                  )}

                  {!!procedure.preOpNote && <CardNote note={procedure.preOpNote} highlightPink />}
                  {!!procedure.anesthesiologistNotes && <CardNote note={procedure.anesthesiologistNotes} highlight />}
                  {!!procedure.orNotes && <CardNote note={procedure.orNotes} highlightWarning />}
                  {!hideNotes && !!procedure.notes && (
                    <BodySegment>
                      <HighlightedText className={highlightNotes ? classes.highlightNotes : undefined}>
                        {procedure.notes}
                      </HighlightedText>
                    </BodySegment>
                  )}
                  {!!allergies && (
                    <BodySegment>
                      <HighlightedText className={highlightAllergies ? classes.highlightAllergies : undefined}>
                        {allergies}
                      </HighlightedText>
                    </BodySegment>
                  )}
                </Body>
              )}
              {fullHeight && (
                <StaffMembersBox>
                  <StaffSlotProcedureContext hospitalId={hospitalId} procedureId={procedure.id} date={formattedDate}>
                    <StaffShifts
                      staffShifts={procedure.staffShifts || []}
                      procedure={procedure}
                      isKiosk={isKiosk}
                      editableStaff={canAddStaff}
                      type={'ProcedureShift'}
                    >
                      {canAddStaff && (
                        <StaffShiftMenu procedure={procedure} defaultPrimarySpecialty={PrimarySpecialty.OR} />
                      )}
                    </StaffShifts>
                  </StaffSlotProcedureContext>
                </StaffMembersBox>
              )}
            </Box>
          </Box>
        </Card>
      </StaffShiftProcedureContext>
    );
  }
};

export const useStyles = makeStyles(theme => ({
  wrapper: {
    height: '100%',
    width: '100%',
    backgroundColor: theme.palette.type === 'light' ? theme.palette.common.white : '#222c59',
    borderRadius: '0.16666em',
    boxShadow: `0 0 8px ${theme.palette.type === 'light' ? alpha(theme.palette.common.black, 0.1) : '#121d4d'}`,
    overflow: 'hidden',

    '&:after': {
      content: '',
      display: 'block',
      borderRadius: '0 0 0.16666em 0.16666em',
      position: 'absolute',
      bottom: 0,
      left: '0.5em',
      right: '0.5em',
      height: '0.4em',
      background: 'linear-gradient(0, rgba(42, 52, 93, 1) 0%, rgba(42, 52, 93, 0) 100%)',
    },
  },
  disabled: {
    opacity: 0.3,
    pointerEvents: 'none',
    border: 'none',
    animation: 'none',
  },
  procedureName: {
    color: theme.palette.text.primary,
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 1.2,
    marginTop: '-0.4em',
    marginBottom: '0.2em',
  },
  onClick: {
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'transform 100ms ease-in-out, background-color 150ms ease-in-out',

    '&:hover': {
      backgroundColor:
        theme.palette.type === 'light'
          ? lighten(theme.palette.text.primary, 0.95)
          : lighten(theme.palette.background.default, 0.2),
    },

    '&:active': {
      backgroundColor: alpha(theme.palette.text.primary, 0.075),
    },
  },
  isDpuFromOr: {
    border: '1px solid purple',
  },
  customBorderColor: {
    border: '4px solid transparent',
  },
  completed: {
    opacity: 0.3,

    '&:after': {
      content: '',
      background: 'linear-gradient(0, rgba(34, 44, 87, 1) 0%, rgba(34, 44, 87, 0) 100%)',
    },
  },

  header: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: alpha(theme.palette.background.paper, 0.05),
    fontSize: '1em',
    position: 'relative',
    zIndex: 1,
  },
  stretch: {
    height: '100%',
  },
  row: {
    flexDirection: 'row',
  },
  column: {
    flexDirection: 'column',
    height: '100%',

    '& > *': {
      flex: '1 0 auto',
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  staffMembers: {
    display: 'flex',
    minWidth: 0,
    gap: '0.5em',

    '& > div': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      fontSize: 'inherit',
      fontFamily: 'inherit',
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
  },

  '@keyframes emphasize': {
    '0%': {
      borderColor: 'rgba(255, 0, 0, 0)',
      boxShadow: '0 0 4px rgba(255, 0, 0, 0)',
    },
    '100%': {
      borderColor: 'rgba(255, 0, 0, 1)',
      boxShadow: '0 0 30px rgba(255, 0, 0, 1)',
    },
  },
  helpContainer: {
    border: '3px solid transparent',
    height: '100%',
  },
  helpContainerAnimation: {
    animation: `1.2s $emphasize ease-in alternate infinite`,
  },
  helpContainerBackground: {
    background: theme.palette.background.default,
  },
  highlightNotes: {
    '& > p': {
      fontWeight: 'bold !important',
    },
  },
  highlightAllergies: {
    '& > *': {
      color: `${theme.palette.error.main} !important`,
    },
  },
}));

export default Procedure;
